import type { FC, ReactNode } from 'react'
import type { PageSection } from '@/types'

import { cn } from '@/lib/utils'
import { cms } from '@/lib/cms'

interface SectionIntroProps {
    section: PageSection
    tone?: 'light' | 'dark'
    showSubheading?: boolean
    headingId?: string
    className?: string
}

// Eyebrow, heading and optional subheading of a Page text entry.
const SectionIntro: FC<SectionIntroProps> = ({
    section,
    tone = 'light',
    showSubheading = true,
    headingId,
    className
}): ReactNode => {
    const dark = tone === 'dark'

    return (
        <div className={cn('max-w-2xl', className)}>
            {section.eyebrow && (
                <p className={cn('eyebrow flex items-center gap-4', dark ? 'text-white/55' : 'text-muted-foreground')}>
                    <span aria-hidden='true' className={cn('h-px w-10', dark ? 'bg-white/40' : 'bg-foreground/30')} />
                    <span data-mdlfy-cms={cms('page_sections', section.id, 'eyebrow')}>{section.eyebrow}</span>
                </p>
            )}

            {section.heading && (
                <h2
                    id={headingId}
                    className={cn('display mt-6 text-3xl sm:text-4xl lg:text-[2.75rem]', dark ? 'text-white' : 'text-foreground')}
                >
                    <span data-mdlfy-cms={cms('page_sections', section.id, 'heading')}>{section.heading}</span>
                </h2>
            )}

            {showSubheading && section.subheading && (
                <p className={cn('mt-6 text-lg leading-relaxed', dark ? 'text-white/70' : 'text-muted-foreground')}>
                    <span data-mdlfy-cms={cms('page_sections', section.id, 'subheading')}>{section.subheading}</span>
                </p>
            )}
        </div>
    )
}

export default SectionIntro
